import {css, Typography} from '@mui/material';
import {DlMuiDynamicIcon, DateUtils, RISKINESS_COLORS, GetStylesFunction} from '@wallaby-ui/core';
import React from 'react';

import {ChipsFormatter} from './ChipsFormatter';
import {JustTooltipFormatter} from './JustTooltipFormatter';
import {Formatters, useFormatters} from '../light';
import {AG_GRID_COMPATIBILITY} from '../LightTable';
import {MerieSchema} from 'src/js/common/types';

type DateString = MerieSchema.components['schemas']['PharmaRecommendationPageDto']['followUpDate'];

export const getRecommendationTooltip = (
  isUnpublished: boolean,
  followUpDate: string | undefined,
  lastPublishDate: string | undefined,
  conclusion: string,
  riskiness: any,
  formatters: Formatters
) => {
  const {iconName, tooltip} = getFollowUpDateIconTooltip(followUpDate, formatters, true);
  const {t, tEnum, formatDate} = formatters;

  const getStyles: GetStylesFunction = (options) => ({
    annotationWrapper: css`
      display: flex;
      flex-direction: column;
      gap: 8px;
    `,
    divider: css`
      border-top: 1px solid ${options?.theme?.palette.custom.coolGray['500']};
      width: 100%;
      height: 0;
    `,
    sectionWrapper: css`
      display: flex;
      flex-direction: column;
      align-items: start;
    `,
  });

  const styles = getStyles();

  return (
    <div css={styles.annotationWrapper}>
      {riskiness && (
        <div css={styles.sectionWrapper}>
          <Typography variant='labelBold'>{t('hospitalization.detail.phRec.riskiness')}</Typography>
          <Typography variant='value'>
            <ChipsFormatter
              value={[riskiness]}
              getInfo={(riskiness) => ({
                label: tEnum('Riskiness', riskiness),
                background: RISKINESS_COLORS[riskiness]?.background,
              })}
            />
          </Typography>
        </div>
      )}
      {iconName && tooltip && (
        <div css={styles.sectionWrapper}>
          <Typography variant='labelBold'>{t('filter.patient.pharmaRecommendationFollowUpDate')}</Typography>
          <Typography variant='value' style={{display: 'inline-flex'}}>
            <DlMuiDynamicIcon
              iconName={iconName}
              useFixedViewBox='0 0 14 16'
              noButtonStyles
              style={{padding: 0, marginRight: 4}}
              svgIconStyle={{
                width: 16,
                height: 16,
              }}
            />
            {tooltip}
          </Typography>
        </div>
      )}
      {isUnpublished && (
        <div css={styles.sectionWrapper}>
          <Typography variant='labelBold'>{t('hospitalization.detail.phRec.nis')}</Typography>
          <Typography variant='value' style={{display: 'inline-flex'}}>
            <DlMuiDynamicIcon
              iconName='merieSpikedExclamationIcon'
              useFixedViewBox={14}
              style={{padding: 0, marginRight: 4}}
              svgIconStyle={{
                width: 16,
                height: 16,
              }}
            />
            {t('hospitalization.detail.phRec.unpublished')}
          </Typography>
        </div>
      )}
      {!isUnpublished && (
        <div css={styles.sectionWrapper}>
          <Typography variant='labelBold'>{t('hospitalization.detail.phRec.nis')}</Typography>
          <Typography variant='value' style={{display: 'inline-flex'}}>
            {t('hospitalization.detail.phRec.lastPublishDate')}: {formatDate(lastPublishDate ?? '')}
          </Typography>
        </div>
      )}
      <div css={styles.sectionWrapper}>
        <Typography variant='labelBold'>{t('hospitalization.detail.phRec.conclusion')}</Typography>
        <Typography variant='value' data-ellipsis='3'>
          {conclusion}
        </Typography>
      </div>
    </div>
  );
};

export const getFollowUpDateIconTooltip = (followUpDate: string | undefined, formatters: Formatters, appendDate: boolean = false) => {
  let iconName: string | null = null;
  let tooltip: string | null = null;
  if (followUpDate) {
    const {t, formatDateShort} = formatters;
    const dateFormatted = formatDateShort(followUpDate);
    const nowIsoDateString = DateUtils.toIsoDateStringLocal(new Date());
    if (nowIsoDateString > followUpDate) {
      iconName = 'calendarIconRed';
      tooltip = `${t('hospitalization.detail.phRec.followUpDate.missed')}${appendDate ? `: ${dateFormatted}` : ''}`;
    } else if (nowIsoDateString === followUpDate) {
      iconName = 'calendarIconYellow';
      tooltip = t('hospitalization.detail.phRec.followUpDate.today');
    } else {
      iconName = 'calendarIconGreen';
      tooltip = `${t('hospitalization.detail.phRec.followUpDate.planned')}${appendDate ? `: ${dateFormatted}` : ''}`;
    }
  }
  return {iconName, tooltip};
};

type PhRecommendationFollowUpDateFormatterProps = {
  value: DateString;
  missingValuePlaceholder?: React.ReactNode;
};

export const PhRecommendationFollowUpDateFormatter: React.FC<PhRecommendationFollowUpDateFormatterProps> = ({
  value,
  missingValuePlaceholder = '-',
}) => {
  const formatters = useFormatters();
  const {formatDateShort} = formatters;
  const {iconName, tooltip} = getFollowUpDateIconTooltip(value, formatters);

  if (!value) return <>{missingValuePlaceholder}</>;

  const text = formatDateShort(value);

  return (
    <JustTooltipFormatter
      {...AG_GRID_COMPATIBILITY}
      value={
        <div style={{display: 'inline-flex', alignItems: 'center'}}>
          {iconName && (
            <DlMuiDynamicIcon
              iconName={iconName}
              useFixedViewBox='0 0 14 16'
              noButtonStyles
              style={{padding: 0, marginRight: 4}}
              svgIconStyle={{fontSize: 18}}
            />
          )}
          <span>{text}</span>
        </div>
      }
      tooltipOverride={tooltip ?? text}
    />
  );
};
